/**
 * ContextFileBanner — "this conversation is about <file>", phones only.
 *
 * While a Workspace file is open, every message carries it as context (the toolbar
 * chip toggles that). On desktop the report sits next to the chat, so the user can
 * see what they are talking about. On a phone the open chat covers the report and
 * the small toolbar chip is the only trace — users took the attachment for a bug.
 * This repeats it where it is read first, under the header, with a way out.
 *
 * Purely presentational: each chat implementation keeps owning `contextFileEnabled`,
 * and ✕ is the same action as clicking the chip. Shown by CSS on phones in the main
 * app only (ContextFileBanner.css) — desktop, IVT and Event Hub never see it.
 */
import { useTranslation } from 'react-i18next';
import SafeTrans from '../../i18n/SafeTrans';
import './ContextFileBanner.css';

interface ContextFileBannerProps {
  /** The file being attached, or null/undefined when there is none. */
  fileName?: string | null;
  /** Whether it is currently attached — an excluded file shows no banner. */
  enabled: boolean;
  onExclude: () => void;
}

export default function ContextFileBanner({ fileName, enabled, onExclude }: ContextFileBannerProps) {
  const { t } = useTranslation('chat');
  if (!fileName || !enabled) return null;

  return (
    <div className="chat-panel__context-banner" role="status">
      <span className="chat-panel__context-banner-text">
        <SafeTrans
          t={t}
          i18nKey="contextFile.banner"
          values={{ fileName }}
          components={{ name: <span className="chat-panel__context-banner-name" /> }}
        />
      </span>
      <button
        className="chat-panel__context-banner-close"
        onClick={onExclude}
        aria-label={t('contextFile.stopNamed', { fileName })}
        title={t('contextFile.stop')}
      >
        ✕
      </button>
    </div>
  );
}
